import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiReservedPageTypes,
  ApiYAxisChartType,
} from "@incendium/api";
import { Grid } from "@mui/material";
import { usePageTypes } from "Hooks/usePageTypes";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { useMemo } from "react";
import { AnalyticsSpacing } from "consts";
import {
  CHART_KEY_ORGANIC_SOCIAL_BEST_DAYS,
  CHART_KEY_ORGANIC_SOCIAL_LANDING_PRIMARY_METRICS,
  CHART_KEY_ORGANIC_SOCIAL_PLATFORMS_PRIMARY_METRICS,
  CHART_KEY_ORGANIC_SOCIAL_SESSIONS_COMPOSITION_BY_LANDING,
  CHART_KEY_ORGANIC_SOCIAL_TOP_AUDIENCES,
  CHART_KEY_ORGANIC_SOCIAL_TOP_PATHS,
  CHART_KEY_ORGANIC_SOCIAL_TOP_SESSIONS_BY_LANDING,
} from "features/analytics/constants/chartKeys";

interface IOrganicSocialOverviewProps {
  channelName: string;
}
function OrganicSocialOverview({ channelName }: IOrganicSocialOverviewProps) {
  const { pageTypes } = usePageTypes();
  const blogTag = useMemo(() => {
    return (
      pageTypes.find((pt) => pt._default === ApiReservedPageTypes.BLOG)?.name ||
      ""
    );
  }, [pageTypes]);
  return (
    <Grid container spacing={AnalyticsSpacing} mb={4}>
      <Grid item xs={8} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_SOCIAL_TOP_SESSIONS_BY_LANDING}
          chart={{
            name: `Top 5 Sessions by Landing Location`,
            dimension: [
              ApiDimension.DIMENSION_SESSION_START_BY_DAY,
              ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
            ],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            limit: 5,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: channelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_SOCIAL_SESSIONS_COMPOSITION_BY_LANDING}
          chart={{
            name: `Top Sessions Composition by Landing Location`,
            dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            limit: 15,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: channelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
              showTotals: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 530 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_SOCIAL_LANDING_PRIMARY_METRICS}
          chart={{
            name: `Top Landing Page Location - Primary Metrics`,
            dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,

            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: channelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                  ApiMetric.METRIC_AVERAGE_SESSION_DURATION,
                  ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                  ApiMetric.METRIC_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 530 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_SOCIAL_PLATFORMS_PRIMARY_METRICS}
          chart={{
            name: `Top Platforms - Primary Metrics`,
            dimension: [ApiDimension.DIMENSION_SOURCE],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,

            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: channelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                  ApiMetric.METRIC_AVERAGE_SESSION_DURATION,
                  ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                  ApiMetric.METRIC_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>

      <Grid item xs={4} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_SOCIAL_TOP_AUDIENCES}
          chart={{
            name: `Top Audiences by Sessions & Revenue`,
            dimension: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,

            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: channelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_REVENUE],
                chart: {
                  [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              metricsOrder: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_REVENUE,
              ],
            },
          }}
        />
      </Grid>

      <Grid item xs={4} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_SOCIAL_BEST_DAYS}
          chart={{
            name: `Best Days by Sessions and Revenue`,
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_WEEKDAY],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            orderBy: `${ApiDimension.DIMENSION_SESSION_START_BY_WEEKDAY} ASC`,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: channelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_REVENUE],
                chart: {
                  [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              metricsOrder: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_REVENUE,
              ],
            },
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_SOCIAL_TOP_PATHS}
          chart={{
            name: `Top Paths - Primary Metrics`,
            dimension: [ApiDimension.DIMENSION_PATH],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: channelName,
              },

              {
                key: ApiDimension.DIMENSION_LOCATION_TAGGER,
                value: blogTag,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                  ApiMetric.METRIC_AVERAGE_SESSION_DURATION,
                  ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                  ApiMetric.METRIC_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default OrganicSocialOverview;
